import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, ShieldAlert, Megaphone, Film, Loader2, Download } from "lucide-react";
import { useSession } from "@/context/SessionContext";
import { adsApi } from "@/api/mist";
import { api } from "@/api/client";
import AdMarketplaceBody from "@/components/dashboard/vault/AdMarketplaceBody";

const FOUNDER_EMAIL = "djohnm1972@gmail.com";
const FOUNDER_HANDLE = "johnnywalker";
const GOLD = "#FFCC00";

// Founder-only fast lane to the Ad Marketplace console (manage / pending approvals /
// revenue) without unlocking the whole Secure Vault. Backend already founder-gates
// every /api/ads admin endpoint, so this route is safe regardless of client checks.
export default function FounderAdConsole() {
  const navigate = useNavigate();
  const { user } = useSession();
  const [genBusy, setGenBusy] = useState(false);

  const generateReel = async () => {
    setGenBusy(true);
    try {
      const d = await adsApi.generatePromoReel(window.location.origin);
      const res = await api.get(`/central-core/vault-file/${d.asset_id}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = d.filename || "alix-promo.mp4";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Promo saved to your video vault + downloaded to your device");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate the promo reel");
    } finally {
      setGenBusy(false);
    }
  };

  const isFounder = !!user && (
    ["founder", "admin"].includes(user.role) ||
    (user.handle || "").toLowerCase() === FOUNDER_HANDLE ||
    (user.email || "").toLowerCase() === FOUNDER_EMAIL
  );

  if (!user || !isFounder) {
    return (
      <div data-testid="founder-ads-gate" className="flex h-full w-full flex-col items-center justify-center gap-4 bg-[#0b1020] p-8 text-center">
        <ShieldAlert size={48} style={{ color: GOLD }} />
        <h1 className="font-serif text-2xl text-white">Founder access only</h1>
        <p className="max-w-[280px] font-mono text-[11px] leading-relaxed text-white/50">
          {user ? "This console is restricted to the Misteck founder." : "Sign in as the founder to manage ad approvals."}
        </p>
        <button onClick={() => navigate("/")} data-testid="founder-ads-home"
          className="mt-2 rounded-full px-6 py-3 font-mono text-[11px] font-black uppercase tracking-[0.18em] text-black"
          style={{ backgroundColor: GOLD }}>
          Back to Misteck
        </button>
      </div>
    );
  }

  return (
    <div data-testid="founder-ad-console" className="h-full w-full overflow-y-auto bg-[#0b1020] text-white">
      <div className="mx-auto max-w-[560px] p-4 sm:p-6">
        <button onClick={() => navigate("/")} data-testid="founder-ads-back"
          className="mb-4 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-white/50 hover:text-gold">
          <ArrowLeft size={14} /> Back to Misteck
        </button>
        <div className="mb-5 flex items-center gap-3">
          <div className="grid h-12 w-12 place-items-center rounded-2xl border-2" style={{ borderColor: GOLD }}>
            <Megaphone size={22} style={{ color: GOLD }} />
          </div>
          <div>
            <h1 className="font-serif text-3xl leading-none text-white">Ad Marketplace</h1>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-white/40">Manage · Approve · Revenue</p>
          </div>
        </div>
        <AdMarketplaceBody />

        {/* ALIX promo reel → MP4 vault export */}
        <div data-testid="promo-reel-card" className="mt-6 rounded-2xl border-2 border-gold/40 bg-black/40 p-4">
          <div className="flex items-center gap-2">
            <Film size={16} style={{ color: GOLD }} />
            <h2 className="font-serif text-lg text-white">ALIX Promo Reel</h2>
          </div>
          <p className="mt-1 font-mono text-[10px] leading-relaxed text-white/50">
            The animated 5-character showcase (Create · Edit · Publish · Play · Vault) that runs on your
            landing page & in the games. Save it as an MP4 to your video vault and download it to your device.
          </p>
          <button onClick={generateReel} disabled={genBusy} data-testid="promo-reel-generate"
            className="mt-3 flex w-full items-center justify-center gap-2 rounded-full py-3 font-mono text-[11px] font-black uppercase tracking-widest text-black disabled:opacity-50"
            style={{ backgroundColor: GOLD }}>
            {genBusy ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
            {genBusy ? "Rendering MP4…" : "Generate & download MP4"}
          </button>
          {genBusy && <p className="mt-2 text-center font-mono text-[9px] text-white/40">This takes ~10–15 seconds.</p>}
        </div>
      </div>
    </div>
  );
}
